import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionHeading, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Users2, Building2, Layers, GraduationCap, Sparkles, Cpu, ShieldCheck, Trophy, MessageSquareQuote, ArrowRight, CheckCircle2, Target, Calendar, Flag, ShieldAlert,
} from 'lucide-react';
import { ROADMAP } from '@/data/roadmapContent';
import { HUNDRED_DAY_PLAN } from '@/data/hundredDayPlan';

const PHASE_ICONS = {
  1: Users2,
  2: MessageSquareQuote,
  3: Layers,
  4: GraduationCap,
  5: Sparkles,
  6: Trophy,
  7: Cpu,
  8: ShieldCheck,
  9: Target,
  10: Building2,
};

export default function RoadmapPage() {
  const { t } = useLang();
  const R = ROADMAP;

  return (
    <div>
      {/* HERO */}
      <section className="hero-surface">
        <Container className="py-14 sm:py-20">
          <SectionKicker>Launch Blueprint · Maharashtra Pilot (First State)</SectionKicker>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220] tracking-[-0.01em]">
            {R.title}
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-[#062B5C] font-medium">{R.subtitle}</p>
          <p className="mt-3 max-w-3xl text-[#5B6B82] leading-relaxed">{R.intro}</p>

          {/* Pan-India context callout */}
          <Card className="mt-6 border-[#D7E1EF] bg-white" data-testid="roadmap-pan-india-note">
            <CardContent className="p-5 flex items-start gap-4">
              <div className="h-9 w-9 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C] shrink-0">
                <Layers className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">First-state pilot within a national framework</div>
                <p className="mt-1.5 text-sm text-[#0B1220] leading-relaxed max-w-3xl">
                  NGRP is designed as a pan-India political movement from day one. The Maharashtra launch below is the operating-model pilot \u2014 not a Maharashtra-only party. See the{' '}
                  <Link to="/national" className="text-[#062B5C] font-semibold underline decoration-[#F47B20]/50 underline-offset-2">
                    Pan-India architecture
                  </Link>{' '}for the wave-based national expansion.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Positioning */}
          <Card className="mt-10 border-[#D7E1EF] bg-white">
            <CardContent className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-6">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Core positioning</div>
                <div className="mt-3 font-display text-xl font-semibold text-[#0B1220]">
                  {R.positioning.mission_marathi}
                </div>
                <div className="mt-2 text-[#5B6B82] text-sm">{R.positioning.mission_english}</div>
              </div>
              <div className="lg:col-span-6">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Five public pillars</div>
                <ol className="mt-3 space-y-2 list-decimal list-inside">
                  {R.positioning.pillars.map((p) => (
                    <li key={p} className="text-sm text-[#0B1220]">{p}</li>
                  ))}
                </ol>
                <div className="mt-4 rounded-lg bg-[#FFF7EE] border border-[#F47B20]/30 p-3 text-xs text-[#8A4B12]">
                  {R.positioning.note}
                </div>
              </div>
            </CardContent>
          </Card>
        </Container>
      </section>

      {/* PHASES */}
      <section className="py-16">
        <Container>
          <SectionHeading eyebrow="Ten phases" title="How NGRP will be built, phase by phase" />
          <div className="mt-10 space-y-6" data-testid="roadmap-phases">
            {R.phases.map((ph) => {
              const Icon = PHASE_ICONS[ph.number] || Layers;
              return (
                <Card key={ph.number} className="border-[#D7E1EF] bg-white" data-testid="roadmap-phase-card">
                  <CardContent className="p-6 sm:p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                      <div className="lg:col-span-3">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 rounded-lg bg-[#062B5C] text-white flex items-center justify-center font-semibold">
                            {String(ph.number).padStart(2, '0')}
                          </div>
                          <div className="h-11 w-11 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C]">
                            <Icon className="h-5 w-5" />
                          </div>
                        </div>
                        {ph.window && (
                          <div className="mt-4 inline-flex items-center rounded-full bg-[#EAF6EF] text-[#138A4B] px-2.5 py-1 text-xs font-medium">
                            {ph.window}
                          </div>
                        )}
                        <h3 className="mt-4 font-display text-lg sm:text-xl font-semibold text-[#0B1220]">
                          {ph.title}
                        </h3>
                      </div>
                      <div className="lg:col-span-9 space-y-4">
                        <p className="text-[15px] text-[#0B1220] leading-relaxed">{ph.body}</p>

                        {ph.selection_question && (
                          <div className="rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] p-4">
                            <p className="text-sm text-[#062B5C] italic leading-relaxed">{ph.selection_question}</p>
                          </div>
                        )}

                        {ph.criteria && (
                          <ChipList label="Selection criteria" items={ph.criteria} tone="navy" />
                        )}
                        {ph.teams && (
                          <ChipList label="Functional teams" items={ph.teams} tone="muted" />
                        )}

                        {ph.quote && (
                          <blockquote className="border-l-2 border-[#F47B20] pl-4 py-1 text-[#062B5C] font-medium">
                            “{ph.quote}”
                          </blockquote>
                        )}

                        {ph.pyramid && (
                          <PyramidTrail items={ph.pyramid} />
                        )}
                        {ph.levels_detail && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {ph.levels_detail.map((lv) => (
                              <div key={lv.level} className="rounded-lg border border-[#D7E1EF] bg-[#F5F8FC] p-4">
                                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">{lv.level}</div>
                                <p className="mt-2 text-sm text-[#0B1220] leading-relaxed">{lv.body}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {ph.curriculum && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Curriculum</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {ph.curriculum.map((c) => (
                                <span key={c} className="inline-flex items-center rounded-full border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs font-medium text-[#062B5C]">
                                  {c}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {ph.field_rule && (
                          <div className="rounded-lg bg-[#FFF7EE] border border-[#F47B20]/30 p-4 text-sm text-[#8A4B12] leading-relaxed">
                            {ph.field_rule}
                          </div>
                        )}

                        {ph.program && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Format</div>
                            <div className="mt-2 text-sm text-[#0B1220]">{ph.program}</div>
                          </div>
                        )}
                        {ph.example_question && (
                          <blockquote className="border-l-2 border-[#138A4B] pl-4 py-1 text-[#0B1220]">
                            {ph.example_question}
                          </blockquote>
                        )}

                        {ph.tiers && (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {ph.tiers.map((tier) => (
                              <div key={tier.name} className="rounded-lg border border-[#D7E1EF] bg-white p-4">
                                <div className="font-display font-semibold text-[#0B1220]">{tier.name}</div>
                                <p className="mt-1 text-xs text-[#5B6B82] leading-relaxed">{tier.body}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {ph.capabilities && (
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Citizen can</div>
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {ph.capabilities.map((c) => (
                                <div key={c} className="flex items-center gap-2 text-sm text-[#0B1220]">
                                  <CheckCircle2 className="h-3.5 w-3.5 text-[#138A4B]" />
                                  {c}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {ph.map && (
                          <div className="rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] p-4 text-sm text-[#0B1220]">
                            {ph.map}
                          </div>
                        )}

                        {ph.examples && (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {ph.examples.map((ex) => (
                              <div key={ex.role} className="rounded-lg border border-[#D7E1EF] bg-white p-4">
                                <div className="text-xs font-semibold text-[#062B5C]">{ex.role}</div>
                                <p className="mt-1 text-xs text-[#5B6B82] leading-relaxed">{ex.target}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {ph.pipeline && (
                          <PyramidTrail items={ph.pipeline} numbered />
                        )}
                        {ph.principle && (
                          <blockquote className="border-l-2 border-[#F47B20] pl-4 py-1 text-[#0B1220]">
                            <span className="font-medium">Principle: </span>{ph.principle}
                          </blockquote>
                        )}

                        {ph.deliverable && (
                          <div className="rounded-lg bg-[#062B5C] text-white p-4">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">Deliverable</div>
                            <div className="mt-1 font-display text-lg font-semibold">{ph.deliverable}</div>
                            {ph.report_stats && (
                              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {ph.report_stats.map((s) => (
                                  <div key={s.label} className="rounded-lg bg-white/10 border border-white/15 p-3">
                                    <div className="kpi-num text-xl font-semibold">{s.value}</div>
                                    <div className="text-[10px] mt-0.5 uppercase tracking-[0.12em] text-white/80">{s.label}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {ph.closing && (
                              <p className="mt-4 text-sm text-white/85">{ph.closing}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </Container>
      </section>

      {/* 100-Day Maharashtra Launch Plan */}
      <HundredDaySection />

      {/* Growth engine */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading
            eyebrow="The growth engine"
            title="How NGRP scales, level by level"
            subtitle="Maharashtra first — then national expansion, once the operating model is proven."
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="roadmap-growth-engine">
            {ROADMAP.growth_engine.map((step, i) => (
              <div
                key={step}
                className="relative rounded-xl border border-[#D7E1EF] bg-white p-5"
              >
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#062B5C]">Level {i + 1}</div>
                <div className="mt-2 font-display font-semibold text-[#0B1220] text-sm">{step}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-2xl bg-[#062B5C] text-white p-6 sm:p-8">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">Closing principle</div>
            <p className="mt-3 font-display text-xl sm:text-2xl font-semibold text-white leading-snug">
              {ROADMAP.closing_principle}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/dialogue">
                <Button className="bg-[#F47B20] hover:bg-[#dd6f1d] text-white" data-testid="roadmap-dialogue-cta">
                  Join the Reform Dialogue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/aspirant">
                <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10" data-testid="roadmap-aspirant-cta">
                  Apply to the Leadership Academy
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}

function ChipList({ label, items, tone = 'navy' }) {
  const toneCls = tone === 'navy'
    ? 'bg-[#062B5C] text-white'
    : 'bg-white text-[#062B5C] border border-[#D7E1EF]';
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">{label}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map((it) => (
          <span key={it} className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium ${toneCls}`}>
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

function PyramidTrail({ items, numbered = false }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {items.map((it, i) => (
        <React.Fragment key={it}>
          <span className="inline-flex items-center gap-1.5 rounded-md border border-[#D7E1EF] bg-white px-2.5 py-1.5 text-xs font-medium text-[#062B5C]">
            {numbered && (
              <span className="h-4 w-4 rounded-full bg-[#062B5C] text-white text-[10px] flex items-center justify-center">{i + 1}</span>
            )}
            {it}
          </span>
          {i < items.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-[#062B5C]/50" />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 100-Day Maharashtra Launch Plan
// ---------------------------------------------------------------------------
function HundredDaySection() {
  const P = HUNDRED_DAY_PLAN;
  return (
    <>
      {/* Intro + North star */}
      <section className="py-16 bg-[#F5F8FC]">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <SectionKicker>Days 1 – 100</SectionKicker>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-semibold text-[#0B1220] tracking-[-0.01em]">
                {P.title}
              </h2>
              <p className="mt-4 text-[#5B6B82] leading-relaxed max-w-2xl">{P.subtitle}</p>
              <p className="mt-3 text-[#5B6B82] leading-relaxed max-w-2xl">{P.intro}</p>
            </div>
            <div className="lg:col-span-5">
              <Card className="border-[#D7E1EF] bg-[#062B5C] text-white">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
                    <Flag className="h-3.5 w-3.5" /> {P.north_star.label}
                  </div>
                  <p className="mt-3 text-white/95 leading-relaxed text-[15px]">{P.north_star.body}</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Targets strip */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="hundred-day-targets">
            {P.targets.map((tg) => (
              <div key={tg.label} className="rounded-xl border border-[#D7E1EF] bg-white p-4">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#062B5C]">{tg.note}</div>
                <div className="kpi-num mt-2 text-2xl font-semibold text-[#0B1220]">{tg.value}</div>
                <div className="mt-1 text-xs text-[#5B6B82]">{tg.label}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Week-by-week */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="Week-by-week playbook" title="What NGRP does, week by week" />
          <div className="mt-10 space-y-5" data-testid="hundred-day-weeks">
            {P.weeks.map((w) => (
              <Card key={w.range} className="border-[#D7E1EF] bg-white" data-testid="hundred-day-week-card">
                <CardContent className="p-6 sm:p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-3">
                      <div className="inline-flex items-center gap-2 rounded-full bg-[#F5F8FC] border border-[#D7E1EF] px-3 py-1.5 text-xs font-semibold text-[#062B5C]">
                        <Calendar className="h-3.5 w-3.5" />
                        {w.range}
                      </div>
                      <div className="mt-2 text-xs text-[#5B6B82]">{w.days}</div>
                      <h3 className="mt-4 font-display text-lg sm:text-xl font-semibold text-[#0B1220] leading-snug">
                        {w.title}
                      </h3>
                    </div>
                    <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <PlanBlock label="Whom to approach" items={w.approach} tone="navy" />
                      <PlanBlock label="What to convene" items={w.convene} tone="orange" />
                      <PlanBlock label="Deliverables" items={w.deliverables} tone="green" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </Container>
      </section>

      {/* Guardrails */}
      <section className="py-16">
        <Container>
          <SectionHeading
            eyebrow="Non-negotiables"
            title="Five guardrails for the 100-day launch"
            subtitle="These are the internal disciplines that separate an institution from a personality-driven organisation."
          />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
            {P.guardrails.map((g, i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border border-[#D7E1EF] bg-white p-4">
                <span className="h-8 w-8 rounded-lg bg-[#FFF1E8] border border-[#F47B20]/30 flex items-center justify-center text-[#F47B20] shrink-0">
                  <ShieldAlert className="h-4 w-4" />
                </span>
                <p className="text-sm text-[#0B1220] leading-relaxed">{g}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-xl border border-[#D7E1EF] bg-[#F5F8FC] p-5">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">After Day 100</div>
            <p className="mt-2 text-sm text-[#0B1220] leading-relaxed">{P.next_365}</p>
          </div>
        </Container>
      </section>
    </>
  );
}

function PlanBlock({ label, items, tone = 'navy' }) {
  const toneMap = {
    navy: { chip: 'text-[#062B5C]', bullet: 'bg-[#062B5C]' },
    orange: { chip: 'text-[#B54708]', bullet: 'bg-[#F47B20]' },
    green: { chip: 'text-[#138A4B]', bullet: 'bg-[#138A4B]' },
  };
  const T = toneMap[tone] || toneMap.navy;
  return (
    <div className="rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] p-4">
      <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${T.chip}`}>{label}</div>
      <ul className="mt-3 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#0B1220] leading-relaxed">
            <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${T.bullet}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
